import { formatNumber } from "@/features/reports/lib/format";
import { persianDisplayText } from "@/lib/persianUi";
import { fa } from "../i18n/fa";

const csvEscape = (value) => {
  const text = String(value ?? "");
  const safe = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
};

function exportCsv(filename, rows) {
  const content = `\uFEFF${rows.map((row) => row.map(csvEscape).join(",")).join("\r\n")}`;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function GeoReports({
  summary,
  cities,
  selectedProvince,
  selectedProvinceName,
  hideEmptyProvinces,
}) {
  const provinces = [...summary.provinces]
    .filter((province) => !hideEmptyProvinces || province.kg > 0)
    .sort((left, right) => right.kg - left.kg);
  const provinceRows = [
    [fa.geo.province, fa.geo.volume, fa.geo.records],
    ...provinces.map((province) => [
      persianDisplayText(province.name, 'نامشخص'),
      province.kg,
      province.records,
    ]),
    [fa.geo.unassigned, summary.unassigned_kg, summary.unassigned_records],
  ];
  const cityRows = [
    [fa.geo.city, fa.geo.province, fa.geo.volume, fa.geo.records],
    ...cities.cities.map((city) => [
      persianDisplayText(city.name, 'نامشخص'),
      persianDisplayText(city.province_name, 'نامشخص'),
      city.kg,
      city.records,
    ]),
    ...cities.unassigned_destinations.map((item) => [
      `${fa.geo.unassigned}: ${persianDisplayText(item.destination, fa.geo.emptyDestination)}`,
      fa.geo.unassigned,
      item.kg,
      item.records,
    ]),
  ];

  return (
    <div className="tr-grid tr-geo-reports">
      <section className="tr-card tr-panel tr-import-table-card">
        <div className="tr-geo-report-heading">
          <h2 className="tr-title">{fa.geo.provinceReport}</h2>
          <button
            type="button"
            className="tr-geo-text-button"
            onClick={() => exportCsv("گزارش-استانی.csv", provinceRows)}
          >
            {fa.geo.exportCsv}
          </button>
        </div>
        <div className="tr-import-table-wrap">
          <table className="tr-import-table">
            <thead><tr>
              <th>{fa.geo.province}</th><th>{fa.geo.volume}</th><th>{fa.geo.records}</th>
            </tr></thead>
            <tbody>
              {provinces.map((province) => (
                <tr key={province.code}>
                  <td>{persianDisplayText(province.name, 'نامشخص')}</td><td>{formatNumber(province.kg)}</td><td>{formatNumber(province.records)}</td>
                </tr>
              ))}
              <tr className="tr-geo-unassigned">
                <td>{fa.geo.unassigned}</td>
                <td>{formatNumber(summary.unassigned_kg)}</td>
                <td>{formatNumber(summary.unassigned_records)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="tr-card tr-panel tr-import-table-card">
        <div className="tr-geo-report-heading">
          <h2 className="tr-title">
            {fa.geo.cityReport}
            {selectedProvince ? ` · ${selectedProvinceName}` : ""}
          </h2>
          {cities.has_city_data && (
            <button
              type="button"
              className="tr-geo-text-button"
              onClick={() => exportCsv("گزارش-شهری.csv", cityRows)}
            >
              {fa.geo.exportCsv}
            </button>
          )}
        </div>
        {!cities.has_city_data ? (
          <p className="tr-note">{fa.geo.noCityData}</p>
        ) : cityRows.length > 1 ? (
          <div className="tr-import-table-wrap">
            <table className="tr-import-table">
              <thead><tr>
                <th>{fa.geo.city}</th><th>{fa.geo.province}</th><th>{fa.geo.volume}</th><th>{fa.geo.records}</th>
              </tr></thead>
              <tbody>
                {cities.cities.map((city) => (
                  <tr key={`${city.province_code}-${city.name}`}>
                    <td>{persianDisplayText(city.name, 'نامشخص')}</td><td>{persianDisplayText(city.province_name, 'نامشخص')}</td>
                    <td>{formatNumber(city.kg)}</td><td>{formatNumber(city.records)}</td>
                  </tr>
                ))}
                {cities.unassigned_destinations.map((item) => (
                  <tr className="tr-geo-unassigned" key={`unassigned-${item.destination}`}>
                    <td>{fa.geo.unassigned}: {persianDisplayText(item.destination, fa.geo.emptyDestination)}</td>
                    <td>{fa.geo.unassigned}</td><td>{formatNumber(item.kg)}</td><td>{formatNumber(item.records)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="tr-note">{fa.geo.noCityRecords}</p>
        )}
      </section>
    </div>
  );
}
